import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SetupStep } from '../../reducer/integration.reducer';

/**
 * Modal step shown to the user. Local view state derived from `pendingSetup.step`.
 *
 * Same three-step shape as the Abacus connect modal: welcome (form) → testing
 * (spinner while the BE mints the token + runs the smoke test) → error. No `created`
 * step: on success the manage modal takes over and this modal closes.
 *
 * The reducer uses `awaiting_consent` for the "BE is testing the credentials" phase
 * (a shared string across vendors); we map it to the local `'testing'` step here.
 */
export type TopalModalViewStep = 'welcome' | 'testing' | 'error';

@Component({
    selector: 'aumico-topal-connect-modal',
    templateUrl: './topal-connect-modal.component.html',
    styleUrls: ['./topal-connect-modal.component.scss'],
})
export class TopalConnectModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public step: TopalModalViewStep = 'welcome';
    public errorMessage: string | null = null;

    /** Form values: local to the modal until submitted. NOT in NgRx until dispatch. */
    public displayName = '';
    public serverUrl = '';
    public username = '';
    public password = '';
    /** Toggles the visual masking on the password field. */
    public showPassword = false;

    public readonly vendorDefinition = VENDOR_DEFINITIONS.find(
        (v) => v.vendor === IntegrationVendor.TOPAL,
    );

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.integrationProvider
            .getPendingSetup$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((setup) => {
                this.errorMessage = setup.error;

                if (setup.vendor !== IntegrationVendor.TOPAL) return;

                // On success: the manage modal takes over. Close this immediately.
                if (setup.step === 'created') {
                    this.closed.emit();
                    return;
                }

                this.step = this.viewStepFor(setup.step);
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * All four fields required. Display name distinguishes connections in the manage
     * modal's server list. Server URL must look like an origin (basic regex; a port is
     * allowed, a path is not — the BE normalizes to origin anyway), the rest are
     * non-empty strings.
     */
    public get isFormValid(): boolean {
        const urlOk = /^https?:\/\/[^/\s]+$/i.test(this.serverUrl.trim());
        return (
            this.displayName.trim().length > 0 &&
            urlOk &&
            this.username.trim().length > 0 &&
            this.password.length > 0
        );
    }

    public onSubmit(): void {
        if (!this.isFormValid) return;
        this.integrationProvider.startTopalConnect({
            displayName: this.displayName.trim(),
            serverUrl: this.serverUrl.trim(),
            username: this.username.trim(),
            password: this.password,
        });
    }

    public onTogglePassword(): void {
        this.showPassword = !this.showPassword;
    }

    public onRetryFromError(): void {
        // Clear the reducer's pendingSetup slice FIRST: otherwise the next
        // `getPendingSetup$()` emission re-maps `step: 'error'` and yanks us straight
        // back to the error view. cancelSetup wipes the slice without closing the modal.
        this.integrationProvider.cancelSetup();
        this.errorMessage = null;
        this.step = 'welcome';
    }

    public onCancel(): void {
        this.integrationProvider.cancelSetup();
        this.closed.emit();
    }

    private viewStepFor(reducerStep: SetupStep): TopalModalViewStep {
        switch (reducerStep) {
            case 'awaiting_consent':
                return 'testing';
            case 'error':
                return 'error';
            case 'created':
                // Handled separately by closing the modal: should never render.
                return 'welcome';
            case 'idle':
            default:
                return 'welcome';
        }
    }
}
